import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { message } from "antd";
import moment from "moment";
import { X, ChevronDown, Link as LinkIcon, MapPin } from "lucide-react";
import { createWellness } from "../../api/Schedule/createWellness";

// Create-event modal — same look and validation as user-web's CreateEventModal,
// but it submits through the admin's existing createWellness call/payload format.

const INITIAL_FORM = {
  title: "",
  type: "WELLNESS_SESSION",
  eventType: "VIRTUAL",
  visibility: "PUBLIC",
  date: "",
  time: "",
  endDate: "",
  endTime: "",
  meetingLink: "",
  venue: "",
  surveyLink: "",
};

const CATEGORY_OPTIONS = [
  { value: "WELLNESS_SESSION", label: "Wellness Session" },
  { value: "HEALTH_CAMPUS", label: "Health Camp" },
  { value: "SURVEY", label: "Survey" },
];

const MODE_OPTIONS = [
  { value: "VIRTUAL", label: "Virtual" },
  { value: "ON_CAMPUS", label: "On Campus" },
];

const VISIBILITY_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "PRIVATE", label: "Private" },
];

const labelCls =
  "block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B] mb-[5px] font-poppins";

const inputCls = (hasError) =>
  `w-full bg-white text-[#171717] text-sm rounded-[10px] px-3 py-[10px] outline-none transition-colors font-poppins border-[1.5px] border-solid ${
    hasError ? "border-[#DC2626]" : "border-[#0F172A]/[0.08] focus:border-[#4B61FF]"
  }`;

const ErrorText = ({ text }) =>
  text ? <p className="text-xs text-[#DC2626] mt-1 mb-0 font-poppins">{text}</p> : null;

const SelectField = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-0.5">
    <label className={labelCls}>{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls(false)} appearance-none pr-9 cursor-pointer`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none"
      />
    </div>
  </div>
);

export default function CreateEventModal({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const todayStr = moment().format("YYYY-MM-DD");
  const isSurvey = form.type === "SURVEY";
  // Surveys are always stored as virtual events (same as the Run a Survey page)
  const mode = isSurvey ? "VIRTUAL" : form.eventType;

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";

    if (!form.date) {
      e.date = "Date is required";
    } else if (form.date < todayStr) {
      e.date = "Event date cannot be in the past";
    }
    if (!form.time) {
      e.time = "Time is required";
    } else if (form.date === todayStr && form.time < moment().format("HH:mm")) {
      e.time = "Event time cannot be in the past";
    }

    if (!form.endDate) e.endDate = "End date is required";
    if (!form.endTime) e.endTime = "End time is required";
    if (form.date && form.time && form.endDate && form.endTime) {
      const start = moment(`${form.date} ${form.time}`, "YYYY-MM-DD HH:mm");
      const end = moment(`${form.endDate} ${form.endTime}`, "YYYY-MM-DD HH:mm");
      if (!end.isAfter(start)) e.endTime = "End must be after the start";
    }

    if (mode === "VIRTUAL" && !isSurvey && !form.meetingLink.trim())
      e.meetingLink = "Meeting link is required for virtual events";
    if (mode === "ON_CAMPUS" && !form.venue.trim())
      e.venue = "Venue is required for on-campus events";
    if (isSurvey && !form.surveyLink.trim())
      e.surveyLink = "Survey link is required for survey events";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Same payload shape/format the Schedule Wellness / Health Camp / Survey pages send
    const startTime = moment(`${form.date} ${form.time}`, "YYYY-MM-DD HH:mm").format(
      "YYYY-MM-DDTHH:mm:ss"
    );
    const endTime = moment(`${form.endDate} ${form.endTime}`, "YYYY-MM-DD HH:mm").format(
      "YYYY-MM-DDTHH:mm:ss"
    );
    const payload = {
      organisationId: localStorage.getItem("organisationId"),
      title: form.title.trim(),
      eventType: mode,
      startTime,
      endTime,
      type: form.type,
      active: true,
      visibility: form.visibility,
      ...(isSurvey && { meetingLink: form.surveyLink.trim() }),
      ...(!isSurvey && mode === "VIRTUAL" && { meetingLink: form.meetingLink.trim() }),
      ...(mode === "ON_CAMPUS" && { venue: form.venue.trim() }),
    };

    setSubmitting(true);
    dispatch(createWellness(JSON.stringify(payload)))
      .then((res) => {
        if (res.payload?.status === "success") {
          message.success(isSurvey ? "Survey Created" : "Event Created");
          onSuccess && onSuccess();
          onClose();
        } else {
          message.error("Something went wrong");
        }
      })
      .catch(() => message.error("Something went wrong"))
      .finally(() => setSubmitting(false));
  };

  const canSubmit =
    !!form.title.trim() && !!form.date && !!form.time && !!form.endDate && !!form.endTime;

  return createPortal(
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-[1000] bg-black/45 backdrop-blur-[4px]"
      />
      {/* Spacing uses px values: Bootstrap's !important p-4/py-5/gap-4/gap-5 would override Tailwind's */}
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-[16px] pointer-events-none">
        {/* Undo the app-wide desktop zoom (--app-zoom, 0.8) so the modal renders at
            the same size as user-web's CreateEventModal */}
        <div
          className="pointer-events-auto bg-white w-full max-w-[512px] rounded-[20px] shadow-[0_30px_80px_rgba(0,0,0,0.25)] flex flex-col max-h-[90vh] overflow-hidden font-poppins"
          style={{ zoom: "calc(1 / var(--app-zoom, 1))" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-[24px] py-[20px] border-b-[1px] border-solid border-x-0 border-t-0 border-[#0F172A]/[0.08] flex-shrink-0">
            <h2 className="m-0 text-lg font-semibold text-[#171717]">Create New Event</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full border-0 bg-[#0F172A]/[0.04] hover:bg-[#0F172A]/[0.12] text-[#64748B] transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto px-[24px] py-[22px] flex flex-col gap-[20px]">
            <div>
              <label className={labelCls}>Event Title</label>
              <input
                type="text"
                placeholder="e.g. Wellness Webinar"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                className={inputCls(!!errors.title)}
              />
              <ErrorText text={errors.title} />
            </div>

            <div className="grid grid-cols-2 gap-[16px]">
              <SelectField
                label="Category"
                value={form.type}
                options={CATEGORY_OPTIONS}
                onChange={(v) => update("type", v)}
              />
              <div className="flex flex-col gap-0.5">
                <label className={labelCls}>Mode</label>
                <div
                  className={`flex bg-[#F1F5F9] rounded-[10px] p-[3px] border-[1.5px] border-solid border-[#0F172A]/[0.08] ${
                    isSurvey ? "opacity-75" : ""
                  }`}
                >
                  {MODE_OPTIONS.map((opt) => {
                    const active = mode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={isSurvey}
                        onClick={() => update("eventType", opt.value)}
                        className={`flex-1 py-[7px] text-[13px] font-semibold rounded-lg border-0 transition-all ${
                          isSurvey ? "cursor-not-allowed" : "cursor-pointer"
                        } ${
                          active
                            ? "bg-white text-[#4B61FF] shadow-[0_1px_5px_rgba(0,0,0,0.10)]"
                            : "bg-transparent text-[#64748B]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {mode === "VIRTUAL" && !isSurvey && (
              <div>
                <label className={`${labelCls} flex items-center gap-1`}>
                  <LinkIcon size={11} /> Meeting Link
                </label>
                <input
                  type="url"
                  placeholder="https://zoom.us/j/..."
                  value={form.meetingLink}
                  onChange={(e) => update("meetingLink", e.target.value)}
                  className={inputCls(!!errors.meetingLink)}
                />
                <ErrorText text={errors.meetingLink} />
              </div>
            )}

            {mode === "ON_CAMPUS" && (
              <div>
                <label className={`${labelCls} flex items-center gap-1`}>
                  <MapPin size={11} /> Venue Address
                </label>
                <input
                  type="text"
                  placeholder="e.g. City Hospital Annex, Mumbai"
                  value={form.venue}
                  onChange={(e) => update("venue", e.target.value)}
                  className={inputCls(!!errors.venue)}
                />
                <ErrorText text={errors.venue} />
              </div>
            )}

            {isSurvey && (
              <div>
                <label className={labelCls}>Survey Link</label>
                <input
                  type="url"
                  placeholder="https://forms.google.com/..."
                  value={form.surveyLink}
                  onChange={(e) => update("surveyLink", e.target.value)}
                  className={inputCls(!!errors.surveyLink)}
                />
                <ErrorText text={errors.surveyLink} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-[16px]">
              <div>
                <label className={labelCls}>Date</label>
                <input
                  type="date"
                  min={todayStr}
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                  className={inputCls(!!errors.date)}
                />
                <ErrorText text={errors.date} />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => update("time", e.target.value)}
                  className={inputCls(!!errors.time)}
                />
                <ErrorText text={errors.time} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-[16px]">
              <div>
                <label className={labelCls}>End Date</label>
                <input
                  type="date"
                  min={form.date || todayStr}
                  value={form.endDate}
                  onChange={(e) => update("endDate", e.target.value)}
                  className={inputCls(!!errors.endDate)}
                />
                <ErrorText text={errors.endDate} />
              </div>
              <div>
                <label className={labelCls}>End Time</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => update("endTime", e.target.value)}
                  className={inputCls(!!errors.endTime)}
                />
                <ErrorText text={errors.endTime} />
              </div>
            </div>

            <SelectField
              label="Visibility"
              value={form.visibility}
              options={VISIBILITY_OPTIONS}
              onChange={(v) => update("visibility", v)}
            />
          </div>

          {/* Footer */}
          <div className="px-[24px] py-[16px] border-t-[1px] border-solid border-x-0 border-b-0 border-[#0F172A]/[0.08] flex-shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className={`w-full py-[13px] rounded-xl border-0 bg-[#0F172A] hover:bg-[#334155] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${
                submitting || !canSubmit ? "opacity-45 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-solid border-white/25 border-t-white rounded-full animate-spin" />
                  Creating…
                </>
              ) : (
                "Create Event"
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
